import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  TableCellLayout,
  Button,
  Input,
  Spinner,
  useTableFeatures,
  useTableSort,
  createTableColumn,
} from "@fluentui/react-components";
import { CiFilter } from "react-icons/ci";
import { DeleteRegular, Add20Filled, EditRegular } from "@fluentui/react-icons";
import { useMsal } from "@azure/msal-react";
import { useTranslation } from "react-i18next";
import "../CSS/ListAgentsProfilesComponent.css";
import { DeleteAgentProfile } from "../../Services/HeedifyServices";
import { ConfirmAlert } from "../GenericComponent/ConfirmAlert";
import { useNavigate } from "react-router-dom";
import {getAccessToken} from '../../Services/GraphServices'
import { useAbility } from "../../Contexts/AbilityContext";
import { useNotification } from "../GenericComponent/NotificationSystem";
const ListAgentProfilesComponent = (props) => {
  const { instance, accounts } = useMsal();
  const activeAccount = accounts[0];
  const tenantId = props.ACTIVE_TENANT || activeAccount?.tenantId;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const ability = useAbility();
  const { showSuccess, showError } = useNotification();
  const SCOPES_NAMES = props.SCOPES_NAMES || [];

  const [profilesRows, setProfilesRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [deletingProfileId, setDeletingProfileId] = useState(null);
  const [filterVisible, setFilterVisible] = useState({
    DISPLAY_NAME: false,
    REGION: false,
  });

  const [filters, setFilters] = useState({
    DISPLAY_NAME: "",
    REGION: "",
  });

  useEffect(() => {
    if (props.AGENT_PROFILES && Object.keys(props.AGENT_PROFILES).length > 0) {
      const profilesArray = Object.values(props.AGENT_PROFILES);
      // 🔐 Calculer permissions pour chaque profil selon son scope
      const profilesWithPermissions = profilesArray.map(profile => {
        const profileScope = profile.scope || "default";
        return {
          ...profile,
          permissions: {
            main: {
              canCreate: ability?.can("create", `AgentProfile.main@${profileScope}`),
              canRead: ability?.can("read", `AgentProfile.main@${profileScope}`),
              canUpdate: ability?.can("update", `AgentProfile.main@${profileScope}`),
              canDelete: ability?.can("delete", `AgentProfile.main@${profileScope}`),
            },
          },
        };
      });
      setProfilesRows(profilesWithPermissions);
      setFilteredRows(profilesWithPermissions);
    }
  }, [props.AGENT_PROFILES, ability]);

  // --- Filtering logic (with date fix) ---
  const applyFilters = (rows, filters) => {
    return rows.filter((profile) => {
      const matchName = profile.displayName
        ?.toLowerCase()
        .includes(filters.DISPLAY_NAME.toLowerCase());
      const matchRegion = profile.region
        ?.toLowerCase()
        .includes(filters.REGION.toLowerCase());

      // --- Fixed date filtering ---
      let matchCreatedAt = true;
      // 🔐 Filtrer par permission canRead
      const hasReadPermission = profile.permissions?.main?.canRead;

      return matchName && matchRegion && matchCreatedAt && hasReadPermission;
    });
  };

  useEffect(() => {
    const newFiltered = applyFilters(profilesRows, filters);
    setFilteredRows(newFiltered);
  }, [filters, profilesRows]);
  // --- Sorting ---()
  const columns = [
    createTableColumn({
      columnId: "DISPLAY_NAME",
      compare: (a, b) =>
        a.displayName?.localeCompare(b.displayName || "") ?? 0,
    }),
    createTableColumn({
      columnId: "REGION",
      compare: (a, b) => a.region?.localeCompare(b.region || "") ?? 0,
    }),
    createTableColumn({ columnId: "ACTION" }),
  ];

  const {
    getRows,
    sort: { getSortDirection, toggleColumnSort, sort },
  } = useTableFeatures({ columns, items: filteredRows }, [
    useTableSort({
      defaultSortState: { sortColumn: "DISPLAY_NAME", sortDirection: "ascending" },
    }),
  ]);

  const rows = useMemo(() => sort(getRows()), [sort, getRows]);

  const headerSortProps = (columnId) => ({
    onClick: (e) => toggleColumnSort(e, columnId),
    sortDirection: getSortDirection(columnId),
  });

  const handleFilterChange = (columnKey, value) => {
    setFilters((prev) => ({ ...prev, [columnKey]: value }));
  };

  const toggleFilterVisibility = (column) => {
    setFilterVisible((prev) => ({ ...prev, [column]: !prev[column] }));
  };
  // --- Delete profile ---
  const removeProfile = (key, profileId) => {
    setDeletingProfileId(profileId);
    getAccessToken(instance, activeAccount)
    .then((token) => {
      return DeleteAgentProfile(tenantId, profileId,token);
    })
    .then((response) => {
      if(response.success && response.message === "deleted") {
        // Mettre à jour Redux pour supprimer le profil
        const updatedProfiles = { ...props.AGENT_PROFILES };
        delete updatedProfiles[profileId];
        props.setAgentProfiles(updatedProfiles);
        
        // Mettre à jour l'état local
        setProfilesRows((prev) => prev.filter((profile) => profile.id !== profileId));
        showSuccess(t("AGENT_PROFILE_DELETED_SUCCESS"));
      } else {
        showError(t("AGENT_PROFILE_DELETE_FAILED"));
      }
    })
    .catch((error) => {
      console.error("Erreur de suppression :", error);
      showError(t("AGENT_PROFILE_DELETE_ERROR"));
    })
    .finally(() => {
      setDeletingProfileId(null);
      setSelectedProfile(null);
    });
  };

  const confirmRemoveProfile = (profile, index) => {
    setSelectedProfile({ ...profile, key: index });
  };

  const addProfile = () => {
    navigate(`/agentsProfiles/newProfile`);
  };

  const selectProfile = (event, profileId) => {
    navigate(`/agentsProfiles/${profileId}`);
  };

  // 🔐 Vérifier si on peut créer un profil dans au moins un scope
  // Si SCOPES_NAMES n'est pas défini ou vide, autoriser (super admin)
  const hasAnyCreatePermission = !SCOPES_NAMES || SCOPES_NAMES.length === 0 
    ? true 
    : SCOPES_NAMES.some(scope => ability?.can("create", `AgentProfile.main@${scope}`));

  return (
    <>
      <div >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <h2>{t("AGENT_PROFILES_LIST")}</h2>
        <Button
          appearance="primary"
          onClick={addProfile}
          className="agent-profile-list-button-style"
      disabled={!hasAnyCreatePermission}
        >
          <Add20Filled /> {t("ADD_NEW_AGENT_PROFILE")}
        </Button>
      </div>

      <div className="table-container">
        <div className="agent-profile-list-table-wrapper">
          <Table
            role="grid"
            aria-label="Agent Profiles Table"
            className="agent-profile-list-table-style"
            sortable
          >
            <TableHeader>
              <TableRow className="agent-profile-list-header-style">
                {/* DISPLAY_NAME */}
                <TableHeaderCell
                  className="agent-profile-list-cell-bold"
                  {...headerSortProps("DISPLAY_NAME")}
                >
                  <div className="agent-profile-list-header-filter-container">
                    <div className="agent-profile-list-div-filter-style">
                      {t("DISPLAY_NAME")}
                      <CiFilter
                        className="agent-profile-list-filter-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFilterVisibility("DISPLAY_NAME");
                        }}
                      />
                      {filterVisible.DISPLAY_NAME && (
                        <Input
                          className="agent-profile-list-input-filter-inline"
                          value={filters.DISPLAY_NAME}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e, data) =>
                            handleFilterChange("DISPLAY_NAME", data.value)
                          }
                          placeholder={t("FILTER")}
                          size="small"
                        />
                      )}
                    </div>
                  </div>
                </TableHeaderCell>

                {/* REGION */}
                <TableHeaderCell
                  className="agent-profile-list-cell-bold"
                  {...headerSortProps("REGION")}
                >
                  <div className="agent-profile-list-header-filter-container">
                    <div className="agent-profile-list-div-filter-style">
                      {t("REGION")}
                      <CiFilter
                        className="agent-profile-list-filter-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFilterVisibility("REGION");
                        }}
                      />
                      {filterVisible.REGION && (
                        <Input
                          className="agent-profile-list-input-filter-inline"
                          value={filters.REGION}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e, data) =>
                            handleFilterChange("REGION", data.value)
                          }
                          placeholder={t("FILTER")}
                          size="small"
                        />
                      )}
                    </div>
                  </div>
                </TableHeaderCell>

                <TableHeaderCell className="agent-profile-list-cell-bold agent-profile-list-cell-right">
                  {t("ACTION")}
                </TableHeaderCell>
              </TableRow>
            </TableHeader>

            <TableBody>
              {rows.map(({ item, rowId }) => (
                <TableRow key={rowId}>
                  <TableCell>
                    <TableCellLayout>{item.displayName}</TableCellLayout>
                  </TableCell>
                  <TableCell>
                    <TableCellLayout>{item.region}</TableCellLayout>
                  </TableCell>
                  <TableCell className="agent-profile-list-cell-right">
                    <Button
                      appearance="subtle"
                      icon={deletingProfileId === item.id ? <Spinner size="tiny" /> : <DeleteRegular />}
                      aria-label="Delete"
                      onClick={() => confirmRemoveProfile(item, rowId)}
                      disabled={!item.permissions?.main?.canDelete || deletingProfileId === item.id}
                    />
                    <Button
                      appearance="subtle"
                      icon={<EditRegular />}
                      aria-label="Edit"
                      onClick={(event) => selectProfile(event, item.id)}
                      disabled={!item.permissions?.main?.canUpdate}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {selectedProfile && (
        <ConfirmAlert
          open={!!selectedProfile}
          name={selectedProfile.displayName}
          onConfirm={() => removeProfile(selectedProfile.key, selectedProfile.id)}
          onCancel={() => setSelectedProfile(null)}
        />
      )}
      </div>
    </>
  );
};

export default ListAgentProfilesComponent;
